<template>
  <div class="h-full overflow-x-auto overflow-y-auto">
    <!-- 表格头部 -->
    <div class="sticky top-0 bg-white dark:bg-gray-800 z-10 border-b border-gray-200 dark:border-gray-700">
      <div class="flex min-w-[900px]">
        <div class="w-16 py-3.5 px-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">ID</div>
        <div class="flex-1 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">功能名称</div>
        <div class="w-28 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">配额类型</div>
        <div class="w-24 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">软限制</div>
        <div class="w-24 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">硬限制</div>
        <div class="w-28 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">重置周期</div>
        <div class="w-32 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">操作</div>
      </div>
    </div>

    <!-- 表格内容 -->
    <div class="divide-y divide-gray-200 dark:divide-gray-700">
      <div v-for="pf in planFeatures" :key="pf.id" class="flex items-center min-w-[900px] hover:bg-indigo-50 dark:hover:bg-indigo-900/30">
        <div class="w-16 py-4 px-3 text-sm text-gray-900 dark:text-gray-100">{{ pf.id }}</div>
        <div class="flex-1 px-3 py-4 text-sm">
          <div class="text-gray-900 dark:text-gray-100 font-medium">{{ displayLocalizedText(pf.feature.name) }}</div>
          <div class="text-xs text-gray-500 dark:text-gray-400 font-mono mt-0.5">{{ pf.feature.featureCode }}</div>
        </div>
        <div class="w-28 px-3 py-4 text-sm">
          <span class="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium whitespace-nowrap"
            :class="getQuotaTypeBadgeClass(pf.quotaType)">
            {{ quotaTypeLabels[pf.quotaType] }}
          </span>
        </div>
        <div class="w-24 px-3 py-4 text-sm text-gray-600 dark:text-gray-400">
          {{ formatLimit(pf.softLimit, pf.quotaType) }}
        </div>
        <div class="w-24 px-3 py-4 text-sm text-gray-900 dark:text-gray-100 font-medium">
          {{ formatLimit(pf.hardLimit, pf.quotaType) }}
        </div>
        <div class="w-28 px-3 py-4 text-sm text-gray-600 dark:text-gray-400">
          {{ pf.resetPeriod || '-' }}
        </div>
        <div class="w-32 px-3 py-4 text-sm flex gap-2">
          <button class="link-btn-edit whitespace-nowrap" @click="handleEdit(pf)"
            title="编辑">
            编辑
          </button>
          <button class="link-btn-delete whitespace-nowrap" @click="handleRemove(pf)"
            title="移除">
            移除
          </button>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-if="planFeatures.length === 0" class="text-center py-12 text-gray-500 dark:text-gray-400">
      该套餐暂无配置功能
    </div>
  </div>
</template>

<script setup lang="ts">
import { QuotaTypeLabels, QuotaType } from '@/types/membership/enums'
import type { PlanFeature } from '@/types/membership/feature'
import { displayLocalizedText } from '@/types/membership/localized'

/**
 * Props
 */
interface Props {
  planFeatures: PlanFeature[]
}

defineProps<Props>()

/**
 * Emits
 */
const emit = defineEmits<{
  edit: [planFeature: PlanFeature]
  remove: [planFeature: PlanFeature]
}>()

/**
 * 配额类型标签
 */
const quotaTypeLabels = QuotaTypeLabels

/**
 * 获取配额类型徽章样式
 */
const getQuotaTypeBadgeClass = (quotaType: QuotaType) => {
  const classMap: Record<QuotaType, string> = {
    [QuotaType.BOOLEAN]: 'bg-purple-50 text-purple-700',
    [QuotaType.DAILY]: 'bg-blue-50 text-blue-700',
    [QuotaType.MONTHLY]: 'bg-green-50 text-green-700',
    [QuotaType.YEARLY]: 'bg-orange-50 text-orange-700',
    [QuotaType.UNLIMITED]: 'bg-gray-50 text-gray-700',
  }
  return classMap[quotaType] || 'bg-gray-50 text-gray-700'
}

/**
 * 格式化限制值
 */
const formatLimit = (limit: number | null, quotaType: QuotaType): string => {
  if (quotaType === QuotaType.BOOLEAN) {
    return limit ? '开启' : '关闭'
  }
  if (quotaType === QuotaType.UNLIMITED) {
    return '无限制'
  }
  if (limit === null || limit === undefined) {
    return '-'
  }
  return limit.toString()
}

/**
 * 处理编辑
 */
const handleEdit = (planFeature: PlanFeature) => {
  emit('edit', planFeature)
}

/**
 * 处理移除
 */
const handleRemove = (planFeature: PlanFeature) => {
  emit('remove', planFeature)
}
</script>
